"use client";

import { useRef } from "react";

import { HOVER_TITLE, TAP_LINK } from "@/lib/theme/interaction";
import { FollowButton } from "./follow-button";
import { categoryRoute, sectionTabRoute } from "@/lib/routes";
import { ScrollEdgeArrows, useScrollEdges } from "./scroll-edges";

const titleStyle: React.CSSProperties = {
  fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "normal",
  color: "rgba(0,0,0,0.5)",
  textTransform: "uppercase",
};

// `color` deliberately lives in a Tailwind class (`text-black` on the anchor)
// rather than here: an inline style beats every CSS rule, so keeping it in this
// object made the tab's `hover:text-*` inert. QA reported the missing hover.
const tabStyle: React.CSSProperties = {
  fontFamily: "'Roboto Condensed', Roboto, 'Noto Sans TC', sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "19.6px",
};

/**
 * Mobile-only section header for subcategory blocks (要聞 / 港聞 / 時事評論 / 信報視頻 etc.).
 * Renders title + 關注 above a horizontally scrollable topic chip row. The
 * trailing chevron (with a blurred backdrop) only appears when the chip row
 * actually overflows; if everything fits the chevron stays hidden.
 */
export function MobileSectionHeader({
  title,
  tabs,
  tabHref,
  titleHref,
  showFollow = true,
}: {
  title: string;
  tabs: string[];
  tabHref: string;
  /** Section-label destination; defaults to the label's own category listing. */
  titleHref?: string;
  showFollow?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Was a single right-hand chevron shown whenever the row overflowed at all —
  // so it stayed on after you had scrolled to the end, and there was never a
  // left one. QA asked for both, each tied to its own direction.
  const edges = useScrollEdges(scrollRef);

  // QA: "category section titles should be clickable … redirect to the category
  // listing page". Resolved from the label itself so every consumer gets it
  // without threading a prop; a label with no listing stays a plain <h2>.
  const labelHref = titleHref ?? categoryRoute(title);

  return (
    <div className="md:hidden flex flex-col gap-[12px]">
      <div className="flex items-center gap-[12px]">
        {labelHref ? (
          <a href={labelHref} className={`${TAP_LINK} w-fit`}>
            <h2 style={titleStyle} className={HOVER_TITLE}>
              {title}
            </h2>
          </a>
        ) : (
          <h2 style={titleStyle}>{title}</h2>
        )}
        {showFollow && <FollowButton />}
      </div>
      <div className="relative flex items-center">
        <div
          ref={scrollRef}
          className="flex-1 min-w-0 overflow-x-auto no-scrollbar flex items-center gap-[16px]"
        >
          {tabs.map((t) => (
            // Same hover vocabulary and same destination rule as the desktop
            // header (see `sectionTabRoute`) — QA asked for the chip hover on
            // the home grids to match /property, where it is a colour change
            // on the chip itself rather than an underline.
            <a
              key={t}
              href={sectionTabRoute(t, tabHref)}
              className={`whitespace-nowrap shrink-0 text-black transition-colors ${TAP_LINK} ${HOVER_TITLE} hkej-tap-self`}
              style={tabStyle}
            >
              {t}
            </a>
          ))}
        </div>
        <ScrollEdgeArrows scroller={scrollRef} edges={edges} />
      </div>
    </div>
  );
}
